/**
 * Member Image Switcher
 *
 * Dynamically switches product card images to show member-specific variant images
 * when a member filter is active on collection pages.
 */

class MemberImageSwitcher {
  constructor() {
    this.init();
  }

  init() {
    this.applyFilters();
    this.observeFacetUpdates();
  }

  /**
   * Apply filters based on current URL parameters
   */
  applyFilters() {
    const activeMembers = this.getActiveMemberFilters();

    if (activeMembers.length > 0) {
      this.showMemberVariants(activeMembers);
    } else {
      this.showMainImages();
    }
  }

  /**
   * Get active member filters from URL parameters
   * Returns array of selected members
   */
  getActiveMemberFilters() {
    const params = new URLSearchParams(window.location.search);
    const members = [];

    for (const [key, value] of params) {
      // Read from 'm' parameter (client-side only, Shopify ignores it)
      if (key === 'm') {
        members.push(value.toLowerCase());
      }
    }

    return members;
  }

  /**
   * Show main product images (no member filter active)
   */
  showMainImages() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;

    // Remove any duplicate cards from multiple member selection
    productGrid.querySelectorAll('.member-duplicate').forEach(el => el.remove());

    const productCards = document.querySelectorAll('.card-wrapper');

    productCards.forEach(card => {
      const mediaElement = card.querySelector('.card__media, .media');
      const img = card.querySelector('.card__media img, .media img');
      const listItem = card.closest('li.grid__item');
      const mainImageUrl = card.dataset.mainImage;

      if (img && mainImageUrl) {
        img.src = mainImageUrl;
        img.srcset = mainImageUrl;
      }

      this.removeSoldOutBadge(card);
      this.enableHover(mediaElement);

      if (listItem) {
        listItem.style.display = '';
      }
    });
  }

  /**
   * Show member variant images (member filter active)
   */
  showMemberVariants(activeMembers) {
    // Only support single member selection
    if (activeMembers.length === 1) {
      this.handleSingleMember(activeMembers[0]);
    }
  }

  /**
   * Handle single member selection
   * Shows member variant images for products that have that member
   */
  handleSingleMember(member) {
    const productCards = document.querySelectorAll('.card-wrapper');
    const memberKey = member.replace(/[^a-z0-9]/g, '');
    const productGrid = document.getElementById('product-grid');

    if (!productGrid) return;

    const inStock = [];
    const soldOut = [];
    const toHide = [];

    productCards.forEach(card => {
      const listItem = card.closest('li.grid__item');
      const memberImageUrl = card.dataset[`member${memberKey}`];
      const memberAvailable = card.dataset[`member${memberKey}Available`];

      if (memberImageUrl) {
        // Product has this member variant - show it with member image
        const mediaElement = card.querySelector('.card__media, .media');
        const img = card.querySelector('.card__media img, .media img');

        if (img) {
          img.src = memberImageUrl;
          img.srcset = memberImageUrl;
        }

        this.disableHover(mediaElement);

        // Add or remove sold-out badge based on availability
        if (memberAvailable === 'false') {
          this.addSoldOutBadge(card);
          soldOut.push(listItem);
        } else {
          this.removeSoldOutBadge(card);
          inStock.push(listItem);
        }

        // Show the product
        if (listItem) {
          listItem.style.display = '';
        }
      } else {
        // Product doesn't have this member - hide it
        if (listItem) {
          listItem.style.display = 'none';
          toHide.push(listItem);
        }
      }
    });

    // Sort: in-stock first, then sold-out, then hidden items at the end
    const sortedItems = [...inStock, ...soldOut, ...toHide];

    sortedItems.forEach(listItem => {
      if (listItem) {
        productGrid.appendChild(listItem);
      }
    });
  }

  /**
   * Handle multiple member selections by duplicating product cards
   */
  handleMultipleMembers(members, productGrid) {
    // Remove any existing duplicates first
    productGrid.querySelectorAll('.member-duplicate').forEach(el => el.remove());

    const originalCards = productGrid.querySelectorAll('li.grid__item:not(.member-duplicate)');

    originalCards.forEach(listItem => {
      const card = listItem.querySelector('.card-wrapper');
      if (!card) return;

      // Collect all member variants for this product
      const memberVariants = members.map(member => {
        const memberKey = member.replace(/[^a-z0-9]/g, '');
        const hasImage = card.dataset[`member${memberKey}`];
        return { member, memberKey, hasImage };
      }).filter(v => v.hasImage); // Only keep variants with images

      // If product has member variants, show them
      if (memberVariants.length > 0) {
        // Update original card with first member
        this.updateCardImage(card, memberVariants[0].memberKey);

        // Create duplicates for additional members
        memberVariants.slice(1).forEach(variant => {
          this.cloneCard(listItem, variant.memberKey);
        });
      }
      // Don't hide products without member variants - Shopify already filtered them
    });
  }

  /**
   * Update card image to show member variant
   */
  updateCardImage(card, memberKey) {
    const mediaElement = card.querySelector('.card__media, .media');
    const img = card.querySelector('.card__media img, .media img');
    const imageUrl = card.dataset[`member${memberKey}`];

    if (img && imageUrl) {
      img.src = imageUrl;
      img.srcset = imageUrl;
    }

    this.disableHover(mediaElement);
  }

  /**
   * Clone a product card for member variant duplication
   */
  cloneCard(listItem, memberKey) {
    const clone = listItem.cloneNode(true);
    clone.classList.add('member-duplicate');

    const clonedCard = clone.querySelector('.card-wrapper');
    this.updateCardImage(clonedCard, memberKey);

    listItem.after(clone);
    return clone;
  }


  /**
   * Enable hover effect on product card
   */
  enableHover(mediaElement) {
    if (mediaElement) {
      mediaElement.classList.remove('media--hover-disabled');
      const secondaryImg = mediaElement.querySelector('img:nth-child(2)');
      if (secondaryImg) {
        secondaryImg.style.display = '';
      }
    }
  }

  /**
   * Disable hover effect on product card
   */
  disableHover(mediaElement) {
    if (mediaElement) {
      mediaElement.classList.add('media--hover-disabled');
      const secondaryImg = mediaElement.querySelector('img:nth-child(2)');
      if (secondaryImg) {
        secondaryImg.style.display = 'none';
      }
    }
  }

  /**
   * Add sold-out badge for member variant
   */
  addSoldOutBadge(card) {
    if (card.querySelector('.member-sold-out-badge')) return;

    const badgeContainer = card.querySelector('.card__badge');
    if (!badgeContainer) return;

    const badge = document.createElement('span');
    badge.className = 'badge badge--bottom-left color-inverse member-sold-out-badge';
    badge.textContent = 'Sold out';
    badgeContainer.appendChild(badge);
  }

  /**
   * Remove sold-out badge for member variant
   */
  removeSoldOutBadge(card) {
    const badge = card.querySelector('.member-sold-out-badge');
    if (badge) {
      badge.remove();
    }
  }

  /**
   * Observe facet filter updates (AJAX)
   */
  observeFacetUpdates() {
    // Listen for facets updated event
    document.addEventListener('facets:updated', () => {
      setTimeout(() => {
        this.applyFilters();
      }, 100);
    });

    // Fallback: Watch for DOM changes in the product grid
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      const observer = new MutationObserver((mutations) => {
        const hasProductChanges = mutations.some(mutation => {
          return mutation.type === 'childList' &&
                 (mutation.target.id === 'product-grid' || mutation.target.id === 'ProductGridContainer');
        });

        if (hasProductChanges) {
          setTimeout(() => {
            this.applyFilters();
          }, 150);
        }
      });

      observer.observe(productGridContainer, {
        childList: true,
        subtree: true
      });
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MemberImageSwitcher();
  });
} else {
  new MemberImageSwitcher();
}
